<template>
  <component :is="layout">
    <div class="tw-w-full">
      <div class="md:tw-px-8 tw-pt-6 tw-pb-10">

        <v-btn text color="primary" class="tw-mb-4" @click="goBack">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ backLabel }}
        </v-btn>

        <div v-if="loadingContext" class="tw-w-full tw-flex tw-justify-center tw-py-20">
          <v-progress-circular :size="56" color="#00AEEF" indeterminate />
        </div>

        <template v-else-if="renewal && renewal.policyNo">
          <header class="page-header tw-mb-6">
            <div>
              <h2 class="page-title">Renew Policy</h2>
              <p class="page-subtitle">{{ renewal.clientName }} · {{ renewal.policyNo }}</p>
              <p class="page-eyebrow">Motor renewal</p>
            </div>
            <!-- renewal live mode -->
          </header>

          <!-- Stepper -->
          <div class="stepper tw-mb-8">
            <div
              v-for="(label, i) in stepLabels"
              :key="i"
              class="stepper-item"
              :class="{
                'stepper-item--done': i < stepIndex,
                'stepper-item--active': i === stepIndex,
              }"
            >
              <div class="stepper-node">
                <v-icon v-if="i < stepIndex" size="14" color="white">mdi-check</v-icon>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="stepper-label">{{ label }}</span>
            </div>
          </div>

          <!-- Step 1: Confirm -->
          <div v-if="step === 'confirm'" class="detail-card">
            <h3 class="detail-section-title">Confirm renewal details</h3>
            <p class="step-hint">{{ copy.confirmHint }}</p>
            <div class="detail-grid tw-mb-6">
              <div class="detail-item">
                <span class="detail-label">{{ copy.clientLabel }}</span>
                <span class="detail-value">{{ renewal.clientName || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Product</span>
                <span class="detail-value">{{ renewal.product || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Policy No</span>
                <span class="detail-value">{{ renewal.policyNo }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Renewal date</span>
                <span class="detail-value">{{ formatDate(renewal.renewalDate) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Renewal premium</span>
                <span class="detail-value highlight">{{ formatCurrency(premiumAmount) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Sum insured</span>
                <span class="detail-value">{{ formatCurrency(renewal.sumInsured) }}</span>
              </div>
            </div>

            <div v-if="risks.length" class="risks-table-wrap tw-mb-6">
              <table class="risks-table">
                <thead>
                  <tr>
                    <th>Risk ID</th>
                    <th>Description</th>
                    <th>Cover</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, idx) in risks" :key="idx">
                    <td>{{ r.riskId || '—' }}</td>
                    <td>{{ r.riskDesc || '—' }}</td>
                    <td>{{ r.coverType || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="isComprehensive" class="benefits-panel tw-mb-6">
              <div class="benefits-panel__head">
                <h4 class="benefits-panel__title">Additional benefits</h4>
                <p class="benefits-panel__hint">{{ copy.benefitsHint }}</p>
              </div>
              <benefit-limit-selector
                compact
                :benefits="chargeableBenefits"
                :selected-addons="primarySelection.addons"
                :custom-limits="primarySelection.limits"
                :vehicles="benefitVehicles"
                :vehicle-selections.sync="vehicleSelections"
                :loading="benefitsLoading"
                :updating="benefitUpdating || processing"
                :premium-lookup="benefitPremiumMap"
                :car-value="carValue"
                :basic-premium="basicPremium"
                :notice-sections="noticeSections"
                @update:selectedAddons="updatePrimarySelection({ addons: $event })"
                @update:customLimits="updatePrimarySelection({ limits: $event })"
                @change="handleBenefitToggle"
              />
            </div>
            <p v-else-if="currentBenefitRows.length" class="tpo-full-note tw-mb-6">
              Third party statutory benefits stay on the policy and cannot be changed at renewal.
            </p>

            <div v-if="isComprehensive" class="benefits-panel valuation-panel tw-mb-6">
              <div class="benefits-panel__head">
                <h4 class="benefits-panel__title">Valuation</h4>
                <p class="benefits-panel__hint">{{ copy.valuationHint }}</p>
              </div>
              <motor-valuer-picker
                hint=""
                :cover-start-date="coverStartIso"
                :location-filter.sync="valuerLocationFilter"
                :search-query.sync="valuerSearchQuery"
                :selected-name="selectedValuer"
                :valuation-date.sync="valuationDate"
                @select="onValuerSelect"
              />
            </div>

            <v-checkbox
              v-model="confirmedWithClient"
              color="#00AEEF"
              hide-details
              class="tw-mt-2"
            >
              <template #label>
                <span class="checkbox-label">{{ copy.confirmCheckbox }}</span>
              </template>
            </v-checkbox>

            <div v-if="actionError" class="renewal-error" role="alert">
              {{ actionError }}
            </div>

            <p v-if="!valuationReady" class="continue-blocker">
              <v-icon x-small color="#b45309" class="tw-mr-1">mdi-information-outline</v-icon>
              Choose a valuer and a valuation date to continue.
            </p>

            <div class="step-actions">
              <v-btn
                color="primary"
                large
                depressed
                :loading="processing"
                :disabled="!confirmedWithClient || !valuationReady"
                @click="onConfirmContinue"
              >
                Continue
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Step 2: Review premium -->
          <div v-else-if="step === 'review'" class="detail-card">
            <h3 class="detail-section-title">Review premium</h3>
            <p class="step-hint">Confirm the amount due for the renewed term before payment.</p>

            <div class="premium-due">
              <span class="premium-due__label">Renewal premium</span>
              <strong class="premium-due__amount">{{ formatCurrency(premiumAmount) }}</strong>
              <p class="premium-due__meta">
                For cover starting {{ formatDate(coverStart) }}<template v-if="startsFromPayment">,
                the day payment is received (the previous cover has ended)</template>
              </p>
            </div>

            <ul v-if="vehiclePremiums.length > 1" class="vehicle-premiums tw-mb-6">
              <li v-for="vehicle in vehiclePremiums" :key="vehicle.riskId" class="vehicle-premiums__row">
                <span>{{ vehicle.regNo || 'Vehicle' }}</span>
                <strong>{{ formatCurrency(vehicle.premium) }}</strong>
              </li>
            </ul>

            <p v-if="isComprehensive && selectedValuer" class="valuation-summary tw-mb-6">
              <v-icon x-small color="#059669" class="tw-mr-1">mdi-car-search-outline</v-icon>
              Valuation by <strong>{{ selectedValuer }}</strong> on {{ formatDate(valuationDate) }}
            </p>

            <div v-if="isComprehensive" class="tw-mb-6">
              <p class="section-sub-label">Select payment plan</p>
              <div class="plan-grid-v2">
                <label
                  v-for="plan in paymentPlans"
                  :key="plan.value"
                  :class="['plan-card-v2', paymentPlan === plan.value ? 'plan-v2-active' : '']"
                >
                  <input
                    v-model="paymentPlan"
                    type="radio"
                    :value="plan.value"
                    class="tw-hidden"
                  />
                  <div class="plan-v2-icon-wrap" :class="paymentPlan === plan.value ? 'plan-v2-icon-active' : ''">
                    <v-icon size="20" :color="paymentPlan === plan.value ? '#00AEEF' : '#94a3b8'">{{ plan.icon }}</v-icon>
                  </div>
                  <div class="plan-v2-body">
                    <p class="plan-v2-name">{{ plan.label }}</p>
                    <p class="plan-v2-amount">{{ plan.amountLine }}</p>
                    <div
                      v-if="plan.value === 'installments' && paymentPlan === 'installments'"
                      class="installment-breakdown tw-mt-2"
                    >
                      <div class="inst-chip inst-chip-now">
                        <span class="inst-chip-pct">40%</span>
                        <span class="inst-chip-label">Now</span>
                        <span class="inst-chip-amount">{{ formatCurrency(installmentFirst) }}</span>
                      </div>
                      <div class="inst-chip-connector">→</div>
                      <div class="inst-chip inst-chip-later">
                        <span class="inst-chip-pct">30%</span>
                        <span class="inst-chip-label">Month 2</span>
                        <span class="inst-chip-amount">{{ formatCurrency(installmentLater) }}</span>
                      </div>
                      <div class="inst-chip-connector">→</div>
                      <div class="inst-chip inst-chip-later">
                        <span class="inst-chip-pct">30%</span>
                        <span class="inst-chip-label">Month 3</span>
                        <span class="inst-chip-amount">{{ formatCurrency(installmentLater) }}</span>
                      </div>
                    </div>
                    <p v-if="plan.value === 'installments'" class="tw-text-xs tw-text-[#94a3b8] tw-mt-2">
                      Balance to be cleared within 2 months.
                    </p>
                  </div>
                  <div v-if="paymentPlan === plan.value" class="plan-v2-check">
                    <v-icon x-small color="white">mdi-check</v-icon>
                  </div>
                </label>
              </div>
            </div>
            <p v-else class="tpo-full-note tw-mb-6">
              Third party renewals require full premium payment upfront.
            </p>

            <div class="pay-amount-box">
              <span>Amount due now</span>
              <strong>{{ formatCurrency(amountDueNow) }}</strong>
            </div>

            <div class="step-actions">
              <v-btn text color="primary" :disabled="processing" @click="step = 'confirm'">Back</v-btn>
              <v-btn color="primary" large depressed :loading="processing" @click="onReviewContinue">
                Proceed to payment
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Step 3: Payment -->
          <div v-else-if="step === 'payment'" class="detail-card">
            <h3 class="detail-section-title">Payment</h3>
            <p class="step-hint">Pay via M-Pesa STK prompt or Paybill.</p>

            <div class="pay-premium-footer tw-mb-6">
              <div class="pay-prem-row">
                <span>Annual premium</span>
                <span class="tw-font-bold tw-text-[#1E4276]">{{ formatCurrency(premiumAmount) }}</span>
              </div>
              <div v-if="isComprehensive && paymentPlan === 'installments'" class="pay-prem-row">
                <span>Payment plan</span>
                <span class="tw-font-semibold tw-text-[#64748B]">40 : 30 : 30 installments</span>
              </div>
              <div class="pay-prem-total">
                <span>Amount due now</span>
                <span class="pay-prem-total-val">{{ formatCurrency(amountDueNow) }}</span>
              </div>
            </div>

            <p class="section-sub-label tw-mb-3">Payment method</p>
            <div class="pay-method-tabs tw-mb-4">
              <button
                type="button"
                class="pay-method-tab"
                :class="{ 'pay-method-tab--active': activePayTab === 'stk' }"
                @click="activePayTab = 'stk'"
              >
                <img src="/mpesa.jpeg" alt="M-Pesa" class="pay-tab-mpesa-logo" />
                <span>M-Pesa STK</span>
              </button>
              <button
                type="button"
                class="pay-method-tab"
                :class="{ 'pay-method-tab--active': activePayTab === 'paybill' }"
                @click="activePayTab = 'paybill'"
              >
                <div class="pay-tab-icon-wrap">
                  <v-icon size="18" :color="activePayTab === 'paybill' ? '#1E4276' : '#94a3b8'">mdi-bank-transfer</v-icon>
                </div>
                <span>Paybill</span>
              </button>
            </div>

            <div v-if="activePayTab === 'stk'">
              <div class="mpesa-panel-card tw-mb-4">
                <div class="mpesa-panel-header">
                  <img src="/mpesa.jpeg" alt="M-Pesa" class="mpesa-panel-logo" />
                  <div>
                    <p class="mpesa-panel-title">Lipa Na M-Pesa</p>
                    <p class="mpesa-panel-sub">{{ copy.stkSub }}</p>
                  </div>
                </div>
                <v-text-field
                  v-model="clientPhone"
                  :label="copy.phoneLabel"
                  placeholder="07XX XXX XXX"
                  outlined
                  hide-details="auto"
                  class="phone-field tw-mt-4"
                  prepend-inner-icon="mdi-cellphone"
                />
              </div>
              <p v-if="paybillFallback" class="stk-timeout-hint">
                {{ copy.stkTimeout }}
              </p>
            </div>

            <div v-else class="paybill-panel-card tw-mb-4">
              <div class="paybill-panel-header">
                <div>
                  <p class="paybill-panel-title">M-Pesa Paybill</p>
                  <p class="paybill-panel-sub">Lipa Na M-Pesa → Pay Bill</p>
                </div>
              </div>
              <div class="paybill-ussd-steps">
                <p class="paybill-ussd-title">Quick steps</p>
                <div class="paybill-ussd-list">
                  <div v-for="(stepText, i) in ussdSteps" :key="i" class="paybill-ussd-item">
                    <span class="paybill-ussd-num">{{ i + 1 }}</span>
                    <span v-html="stepText" />
                  </div>
                </div>
              </div>
            </div>

            <div class="step-actions">
              <v-btn text color="primary" :disabled="processing" @click="step = 'review'">Back</v-btn>
              <v-btn
                v-if="activePayTab === 'stk'"
                color="primary"
                large
                depressed
                :loading="processing"
                :disabled="!clientPhone"
                @click="onPay"
              >
                <v-icon left>mdi-cellphone-wireless</v-icon>
                Send STK Push · {{ formatCurrency(amountDueNow) }}
              </v-btn>
              <v-btn
                v-else
                color="primary"
                large
                depressed
                :loading="processing"
                :disabled="!canValidatePaybill"
                @click="onValidatePaybill"
              >
                <v-icon left>mdi-check-circle-outline</v-icon>
                Validate payment
              </v-btn>
            </div>
          </div>

          <!-- Step 4: Success -->
          <div v-else-if="step === 'success'" class="detail-card success-card">
            <div class="success-icon-wrap">
              <v-icon size="56" color="#38A169">mdi-check-circle</v-icon>
            </div>
            <h3 class="success-title">Renewal successful</h3>
            <p class="success-subtitle">
              {{ copy.successSubtitle }}
            </p>

            <div class="success-details">
              <div class="success-row">
                <span>New policy no</span>
                <strong>{{ result.newPolicyNo || renewal.policyNo }}</strong>
              </div>
              <div class="success-row">
                <span>Transaction ref</span>
                <strong>{{ result.transactionRef || '—' }}</strong>
              </div>
              <div class="success-row">
                <span>Batch ref</span>
                <strong>{{ result.batchId || batchId || '—' }}</strong>
              </div>
              <div class="success-row">
                <span>Premium paid</span>
                <strong>{{ formatCurrency(amountDueNow) }}</strong>
              </div>
            </div>

            <div class="step-actions step-actions--center">
              <v-btn color="primary" large depressed @click="$router.push({ name: renewalsRoute })">
                Back to renewals
              </v-btn>
              <v-btn outlined color="primary" large @click="goToDetails">
                View policy details
              </v-btn>
            </div>
          </div>
        </template>

        <div v-else class="empty-state tw-py-16">
          <v-icon size="60" color="#CBD5E1">mdi-file-document-outline</v-icon>
          <p class="empty-title">Renewal not found</p>
          <p class="empty-subtitle">Open a renewal from the renewals list to continue.</p>
          <v-btn color="primary" outlined class="tw-mt-4" @click="$router.push({ name: renewalsRoute })">
            Back to renewals
          </v-btn>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import Default from '@/components/layout/Default';
import BenefitLimitSelector from '@/components/agent/BenefitLimitSelector';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import {
  loadRenewalFromSession,
  policyToSlug,
  slugToPolicyNo,
  saveRenewalToSession,
  saveRenewalJourneyProgress,
  loadRenewalJourneyProgress,
  clearRenewalJourneyProgress,
  clearAllRenewalJourneyProgress,
  isMotorRenewal,
  isComprehensiveRenewal,
  isLapsedBeyondRenewal,
  effectiveRenewalStart,
  mergeGisRenewalBatchDetail,
  renewalPremium,
  renewalAmountDueNow,
  renewalPaymentTypeCode,
  renewalPaymentReference,
  formatRenewalDate,
  formatRenewalCurrency,
} from '@/utils/agentRenewal';
import {
  upsertRenewalClient,
  buyPlan,
  requestRenewalStkPush,
  pollUntilComplete,
  getRenewalProgress,
  fetchGisRenewalClient,
  fetchGisRenewalBatchDetail,
  buildRenewalPolicyDto,
  confirmRenewalPaybillPayment,
} from '@/services/renewalApi';
import { MOTOR_COVER_COMPREHENSIVE } from '@/constants/motorPrivate';
import MotorValuerPicker from '@/components/motor/MotorValuerPicker';
import { maxValuationDate, toLocalISO } from '@/utils/motorCoverDates';
import {
  fetchMotorChargeableBenefits,
  mapBenefitsForQuoteUpdate,
  renewalNoticeSections,
  displayRenewalBenefitRows,
  renewalQuoteVehicles,
  renewalBenefitVehicles,
  initialVehicleSelections,
  enforceLockedVehicleSelections,
  policyWideSelection,
  syncRenewalVehicleBenefits,
  buildBenefitPremiumMap,
  computeUntaxedBasicPremium,
} from '@/utils/motorBenefits';

const COPY = {
  agent: {
    confirmHint: 'Review the policy information with your client before proceeding.',
    clientLabel: 'Client',
    benefitsHint: 'Windscreen and infotainment come free with comprehensive cover. Tick any paid extras the client wants, or untick one to drop it.',
    confirmCheckbox: 'I have confirmed these details with the client',
    stkSub: 'STK Push — client receives a prompt on their phone',
    phoneLabel: 'Client M-Pesa phone number',
    phoneMissing: 'Enter the client M-Pesa phone number',
    stkSent: 'M-Pesa prompt sent. Ask client to enter PIN.',
    stkTimeout: 'STK timed out. You can switch to Paybill.',
    kraMissing: 'Client KRA PIN is required to start renewal. Confirm client details in GIS.',
    valuationHint: 'Comprehensive cover needs a valuation. Pick a valuer near the client and a date within 30 days of cover start.',
    successSubtitle: 'The policy has been renewed and the new certificate is being generated.',
  },
  customer: {
    confirmHint: 'Check your policy information before you continue.',
    clientLabel: 'Policy holder',
    benefitsHint: 'Windscreen and infotainment come free with your comprehensive cover. Tick any paid extras you want, or untick one to drop it.',
    confirmCheckbox: 'I confirm these details are correct',
    stkSub: 'You will receive a prompt on your phone. Enter your M-Pesa PIN to pay.',
    phoneLabel: 'Your M-Pesa phone number',
    phoneMissing: 'Enter your M-Pesa phone number',
    stkSent: 'M-Pesa prompt sent. Check your phone and enter your M-Pesa PIN.',
    stkTimeout: 'No prompt or it timed out? You can pay through Paybill instead.',
    kraMissing: 'Your KRA PIN is missing on this policy. Contact us to update it, then renew.',
    valuationHint: 'Comprehensive cover needs a valuation. Pick a valuer near you and a date within 30 days of cover start.',
    successSubtitle: 'Your policy has been renewed and your new certificate is being generated.',
  },
};

export default {
  name: 'AgentRenewPolicy',
  components: {
    AgentDefault, Default, BenefitLimitSelector, MotorValuerPicker,
  },
  props: {
    audience: { type: String, default: 'agent' },
  },
  data() {
    return {
      loadingContext: true,
      renewal: null,
      renewalType: 'pending',
      step: 'confirm',
      stepIndex: 0,
      stepLabels: ['Confirm', 'Review', 'Payment', 'Done'],
      confirmedWithClient: false,
      processing: false,
      batchId: null,
      paymentMethod: 'mpesa',
      activePayTab: 'stk',
      paybillFallback: false,
      clientPhone: '',
      paymentPlan: 'full',
      paymentId: null,
      result: {},
      vehicleSelections: {},
      chargeableBenefits: [],
      benefitsLoading: false,
      benefitUpdating: false,
      benefitDetails: [],
      quoteVehicles: [],
      vehiclePremiums: [],
      syncedPremium: null,
      hydrating: false,
      leaving: false,
      actionError: '',
      valuerLocationFilter: '',
      valuerSearchQuery: '',
      selectedValuer: '',
      valuationDate: '',
    };
  },
  computed: {
    isCustomer() {
      return this.audience === 'customer';
    },
    layout() {
      return this.isCustomer ? Default : AgentDefault;
    },
    copy() {
      return COPY[this.isCustomer ? 'customer' : 'agent'];
    },
    renewalsRoute() {
      return this.isCustomer ? 'CustomerRenewals' : 'AgentRenewals';
    },
    paymentChannel() {
      return this.isCustomer ? 'customer' : 'agent';
    },
    agentId() {
      if (this.isCustomer) return undefined;
      return this.$store.getters['auth/authenticatedUser']?.['custom:agent_code'];
    },
    policySlug() {
      return this.$route.params.policyNo || '';
    },
    risks() {
      return Array.isArray(this.renewal?.risks) ? this.renewal.risks : [];
    },
    coverStart() {
      return effectiveRenewalStart(this.renewal?.renewalDate);
    },
    coverStartIso() {
      return toLocalISO(this.coverStart);
    },
    valuationReady() {
      if (!this.isComprehensive) return true;
      if (!this.selectedValuer || !this.valuationDate) return false;
      const max = maxValuationDate(this.coverStartIso);
      return this.valuationDate >= this.coverStartIso && (!max || this.valuationDate <= max);
    },
    startsFromPayment() {
      const due = this.renewal?.renewalDate ? new Date(this.renewal.renewalDate) : null;
      return !!due && !Number.isNaN(due.getTime()) && this.coverStart > due;
    },
    premiumAmount() {
      if (this.syncedPremium != null && Number(this.syncedPremium) > 0) {
        return Number(this.syncedPremium);
      }
      return renewalPremium(this.renewal);
    },
    isComprehensive() {
      return isComprehensiveRenewal(this.renewal);
    },
    amountDueNow() {
      return renewalAmountDueNow(this.premiumAmount, this.paymentPlan, this.isComprehensive);
    },
    installmentFirst() {
      return Math.round(this.premiumAmount * 0.4);
    },
    installmentLater() {
      return Math.round(this.premiumAmount * 0.3);
    },
    paymentPlans() {
      const p40 = this.installmentFirst;
      const p30 = this.installmentLater;
      return [
        {
          value: 'full',
          icon: 'mdi-shield-check',
          label: 'Full payment',
          amountLine: `Pay ${this.formatCurrency(this.premiumAmount)} now`,
        },
        {
          value: 'installments',
          icon: 'mdi-calendar-month',
          label: 'Installments (40 : 30 : 30)',
          amountLine: `Pay ${this.formatCurrency(p40)} now, then ${this.formatCurrency(p30)} × 2`,
        },
      ];
    },
    paymentReference() {
      return renewalPaymentReference(this.renewal, this.risks, this.batchId);
    },
    canValidatePaybill() {
      return !!(this.paymentReference && this.batchId);
    },
    ussdSteps() {
      return [
        'Go to <strong>M-Pesa</strong> — M-Pesa App / SIM Toolkit, or dial <strong>*334#</strong>',
        'Select <strong>Lipa Na M-Pesa</strong> → <strong>Pay Bill</strong>',
        'Enter Business No. <strong>504700</strong>',
        `Enter Account No. <strong>${this.paymentReference || '—'}</strong>`,
        `Enter amount <strong>${this.formatCurrency(this.amountDueNow)}</strong>`,
        'Enter your <strong>M-Pesa PIN</strong> and confirm',
        'You will receive an <strong>M-Pesa confirmation SMS</strong> — then tap <strong>Validate payment</strong>',
      ];
    },
    backLabel() {
      if (this.step === 'confirm' || this.step === 'success') return 'Back to renewals';
      return 'Back';
    },
    currentBenefitRows() {
      return displayRenewalBenefitRows(this.risks);
    },
    noticeSections() {
      return renewalNoticeSections(this.risks);
    },
    carValue() {
      const fromRisk = Number(this.risks[0]?.riskValue);
      if (Number.isFinite(fromRisk) && fromRisk > 0) return fromRisk;
      return Number(this.renewal?.sumInsured) || 0;
    },
    basicPremium() {
      return computeUntaxedBasicPremium(this.premiumAmount);
    },
    benefitPremiumMap() {
      return buildBenefitPremiumMap(this.benefitDetails);
    },
    benefitRisks() {
      return this.risks.length ? this.risks : [{}];
    },
    benefitVehicles() {
      return renewalBenefitVehicles(this.benefitRisks);
    },
    primarySelection() {
      const selection = this.vehicleSelections[this.benefitVehicles[0].key] || {};
      return { addons: selection.addons || [], limits: selection.limits || {} };
    },
    selectedAdditionalBenefits() {
      const { addons, limits } = policyWideSelection(this.vehicleSelections);
      return mapBenefitsForQuoteUpdate(this.chargeableBenefits, addons, limits);
    },
  },
  watch: {
    step(val) {
      const map = {
        confirm: 0,
        review: 1,
        payment: 2,
        success: 3,
      };
      this.stepIndex = map[val] ?? 0;
      this.persistJourney();
    },
    confirmedWithClient() { this.persistJourney(); },
    clientPhone() { this.persistJourney(); },
    activePayTab() { this.persistJourney(); },
    paymentPlan() { this.persistJourney(); },
    batchId() { this.persistJourney(); },
    vehicleSelections: { deep: true, handler() { this.persistJourney(); } },
    valuerLocationFilter() { this.persistJourney(); },
    valuerSearchQuery() { this.persistJourney(); },
    selectedValuer() { this.persistJourney(); },
    valuationDate() { this.persistJourney(); },
    policySlug: {
      immediate: true,
      handler() {
        this.loadContext();
      },
    },
  },
  mounted() {
    this.track('renewal_simulation_started', {
      policy_number: this.policySlug || 'unknown',
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.discardJourney();
    this.leaving = false;
    next();
  },
  beforeRouteLeave(to, from, next) {
    this.discardJourney();
    next();
  },
  methods: {
    track(event, params) {
      if (!this.isCustomer) trackAgentInteraction(this, event, params);
    },

    async findCustomerRenewal() {
      const matches = (r) => r && r.policyNo && policyToSlug(r.policyNo) === this.policySlug;
      const fromStore = () => this.$store.state.renewalReminders?.list?.find(matches);
      if (!fromStore()) {
        await this.$store.dispatch('renewalReminders/refresh', { force: false });
      }
      const found = fromStore();
      if (found) return found;
      const { batch } = this.$route.query;
      return batch ? { policyNo: slugToPolicyNo(this.policySlug), batchNo: batch, effectiveBatchNo: batch } : null;
    },

    discardJourney() {
      this.leaving = true;
      clearAllRenewalJourneyProgress();
    },

    async loadContext() {
      this.hydrating = true;
      this.loadingContext = true;
      const { renewal: sessionRenewal, renewalType } = loadRenewalFromSession(this.policySlug);
      this.renewalType = renewalType;
      let renewal = sessionRenewal;
      if (!renewal && this.isCustomer) {
        renewal = await this.findCustomerRenewal();
      }

      const batchNo = renewal?.effectiveBatchNo || renewal?.batchNo;
      if (renewal && batchNo) {
        const detailRes = await fetchGisRenewalBatchDetail(batchNo);
        if (detailRes.success && detailRes.data) {
          renewal = mergeGisRenewalBatchDetail(renewal, detailRes.data);
          saveRenewalToSession(renewal, renewalType);
        } else if (detailRes.message) {
          this.$notify.error(detailRes.message || 'Could not refresh renewal details from GIS.');
        }
      }

      this.renewal = renewal;

      if (renewal && !isMotorRenewal(renewal)) {
        this.$notify.error('Only motor policies can be renewed in the portal.');
        clearRenewalJourneyProgress(this.policySlug);
        this.renewal = null;
        this.loadingContext = false;
        this.hydrating = false;
        this.$router.replace({ name: this.renewalsRoute });
        return;
      }

      const progress = loadRenewalJourneyProgress(this.policySlug);

      const pastPayment = ['payment', 'success'].includes(progress?.step);
      if (renewal && isLapsedBeyondRenewal(renewal) && !pastPayment) {
        this.$notify.error('This cover ended more than 2 months ago and can no longer be renewed. Start a new quote instead.');
        clearRenewalJourneyProgress(this.policySlug);
        this.renewal = null;
        this.loadingContext = false;
        this.hydrating = false;
        this.$router.replace({ name: this.renewalsRoute });
        return;
      }

      if (renewal && !isComprehensiveRenewal(renewal)) {
        this.paymentPlan = 'full';
      }

      if (progress && renewal) {
        this.step = progress.step || 'confirm';
        this.batchId = progress.batchId || null;
        this.confirmedWithClient = !!progress.confirmedWithClient;
        this.clientPhone = progress.clientPhone || '';
        this.paymentMethod = progress.paymentMethod || 'mpesa';
        this.activePayTab = progress.activePayTab || 'stk';
        this.paybillFallback = !!progress.paybillFallback;
        this.paymentPlan = progress.paymentPlan || 'full';
        if (!isComprehensiveRenewal(renewal)) {
          this.paymentPlan = 'full';
        }
        this.paymentId = progress.paymentId || null;
        this.result = progress.result || {};
        this.quoteVehicles = Array.isArray(progress.quoteVehicles)
          ? progress.quoteVehicles
          : renewalQuoteVehicles({ riskId: progress.quoteRiskId }, this.risks);
        this.vehiclePremiums = progress.vehiclePremiums || [];
        this.syncedPremium = progress.syncedPremium || null;
        this.valuerLocationFilter = progress.valuerLocationFilter || '';
        this.valuerSearchQuery = progress.valuerSearchQuery || '';
        this.selectedValuer = progress.selectedValuer || '';
        this.valuationDate = progress.valuationDate || '';
      } else {
        this.step = 'confirm';
        this.batchId = null;
        this.confirmedWithClient = false;
        this.clientPhone = '';
        this.activePayTab = 'stk';
        this.paybillFallback = false;
        this.paymentPlan = 'full';
        this.paymentId = null;
        this.result = {};
        this.quoteVehicles = [];
        this.vehiclePremiums = [];
        this.syncedPremium = null;
        this.vehicleSelections = {};
        this.valuerLocationFilter = '';
        this.valuerSearchQuery = '';
        this.selectedValuer = '';
        this.valuationDate = '';
      }
      await this.loadRenewalBenefits(progress);
      this.loadingContext = false;
      this.hydrating = false;
      this.persistJourney();
    },

    async loadRenewalBenefits(progress) {
      if (!isComprehensiveRenewal(this.renewal)) {
        this.chargeableBenefits = [];
        this.vehicleSelections = {};
        return;
      }
      this.benefitsLoading = true;
      const res = await fetchMotorChargeableBenefits(MOTOR_COVER_COMPREHENSIVE);
      this.chargeableBenefits = res.success ? res.data : [];
      this.benefitsLoading = false;
      if (progress?.vehicleSelections) {
        this.vehicleSelections = progress.vehicleSelections;
        this.applyLockedRenewalBenefits();
        return;
      }
      const policyWide = Array.isArray(progress?.addonsDynamic)
        ? { addons: progress.addonsDynamic, limits: progress.benefitCustomLimits || {} }
        : null;
      this.vehicleSelections = initialVehicleSelections(this.benefitRisks, this.chargeableBenefits, policyWide);
    },

    applyLockedRenewalBenefits() {
      if (!this.isComprehensive) return;
      this.vehicleSelections = enforceLockedVehicleSelections(
        this.vehicleSelections,
        this.benefitRisks,
        this.chargeableBenefits,
      );
    },

    updatePrimarySelection(patch) {
      this.vehicleSelections = {
        ...this.vehicleSelections,
        [this.benefitVehicles[0].key]: { ...this.primarySelection, ...patch },
      };
    },

    persistJourney() {
      if (this.leaving || this.hydrating || !this.policySlug || !this.renewal?.policyNo) return;
      saveRenewalJourneyProgress(this.policySlug, {
        step: this.step,
        batchId: this.batchId,
        confirmedWithClient: this.confirmedWithClient,
        clientPhone: this.clientPhone,
        paymentMethod: this.paymentMethod,
        activePayTab: this.activePayTab,
        paybillFallback: this.paybillFallback,
        paymentPlan: this.paymentPlan,
        paymentId: this.paymentId,
        result: this.result,
        vehicleSelections: this.vehicleSelections,
        quoteVehicles: this.quoteVehicles,
        vehiclePremiums: this.vehiclePremiums,
        syncedPremium: this.syncedPremium,
        valuerLocationFilter: this.valuerLocationFilter,
        valuerSearchQuery: this.valuerSearchQuery,
        selectedValuer: this.selectedValuer,
        valuationDate: this.valuationDate,
      });
    },

    onValuerSelect(valuer) {
      this.selectedValuer = valuer ? valuer.name : '';
      if (!valuer) this.valuationDate = '';
    },

    formatDate: formatRenewalDate,
    formatCurrency: formatRenewalCurrency,

    goBack() {
      if (this.step === 'payment') {
        this.step = 'review';
        this.persistJourney();
        return;
      }
      if (this.step === 'review') {
        this.step = 'confirm';
        this.persistJourney();
        return;
      }
      // confirm or success → renewals list
      if (this.step === 'success') {
        clearRenewalJourneyProgress(this.policySlug);
      }
      this.$router.push({ name: this.renewalsRoute });
    },

    goToDetails() {
      if (this.isCustomer) {
        this.$router.push({ name: 'SinglePolicy', params: { policyNo: this.renewal.policyNo } });
        return;
      }
      this.$router.push({
        name: 'AgentRenewalDetails',
        params: { policyNo: this.policySlug },
      });
    },

    async handleBenefitToggle({ skipApiSync } = {}) {
      this.persistJourney();
      if (!this.quoteVehicles.length || skipApiSync) return;
      await this.syncSelectedBenefits();
    },

    async syncSelectedBenefits() {
      if (!this.isComprehensive || !this.quoteVehicles.length) return true;
      this.benefitUpdating = true;
      try {
        const res = await syncRenewalVehicleBenefits(
          this.quoteVehicles,
          this.benefitRisks,
          this.chargeableBenefits,
          this.vehicleSelections,
        );
        if (!res.success) {
          if (!res.skipped) {
            this.$notify.error(res.message || 'Could not update benefits');
          }
          return false;
        }
        this.benefitDetails = res.data?.benefits || [];
        this.vehiclePremiums = res.data?.vehicles || [];
        if (res.data?.totalPremium != null) {
          this.syncedPremium = Number(res.data.totalPremium);
        }
        this.persistJourney();
        return true;
      } finally {
        this.benefitUpdating = false;
      }
    },

    showActionError(message) {
      const text = message || 'Could not start renewal';
      this.actionError = text;
      if (this.$notify?.error) {
        this.$notify.error(text, 'Renewal', 12000);
      }
    },

    async onConfirmContinue() {
      if (!this.confirmedWithClient) return;
      if (!this.valuationReady) {
        this.showActionError('Select a valuer and a valuation date within 30 days of cover start.');
        return;
      }
      if (!isMotorRenewal(this.renewal)) {
        this.showActionError('Only motor policies can be renewed in the portal.');
        return;
      }
      this.actionError = '';
      this.applyLockedRenewalBenefits();
      this.processing = true;
      this.track('renewal_confirm', {
        policy_number: this.renewal.policyNo,
      });
      try {
        const batchNo = this.renewal.effectiveBatchNo || this.renewal.batchNo;
        const clientRes = await fetchGisRenewalClient(batchNo);
        const client = clientRes.success ? (clientRes.data || {}) : {};
        const dto = buildRenewalPolicyDto({
          renewal: this.renewal,
          client,
          agentId: this.agentId,
          origin: 'P',
          additionalBenefits: this.selectedAdditionalBenefits,
          valuation: this.isComprehensive ? {
            valuer: this.selectedValuer,
            valuationLocation: this.valuerLocationFilter,
            valuationDate: this.valuationDate,
          } : null,
        });
        if (!dto.kraPinNumber) {
          this.showActionError(this.copy.kraMissing);
          return;
        }
        const res = await upsertRenewalClient(dto);
        const nextBatchId = res.data?.batchId || res.data?.batchNo;
        if (!res.success || !nextBatchId) {
          this.showActionError(res.message || 'Could not start renewal');
          return;
        }
        this.batchId = nextBatchId;
        const vehicles = renewalQuoteVehicles(res.data, this.risks);
        if (vehicles.length) this.quoteVehicles = vehicles;
        this.clientPhone = this.clientPhone || client.mobileNo || client.mobileNumber || '';
        await this.syncSelectedBenefits();
        this.step = 'review';
        this.persistJourney();
      } catch {
        this.showActionError('Could not start renewal. Please try again.');
      } finally {
        this.processing = false;
      }
    },

    async onReviewContinue() {
      this.processing = true;
      try {
        const res = await buyPlan({
          batchId: this.batchId,
          riskId: 0,
          paymentType: renewalPaymentTypeCode(this.paymentPlan),
        });
        if (res.success) {
          this.step = 'payment';
          this.persistJourney();
        } else {
          this.$notify.error(res.message || 'Confirmation failed');
        }
      } catch {
        this.$notify.error('Could not confirm renewal.');
      } finally {
        this.processing = false;
      }
    },

    async onPay() {
      if (!this.clientPhone) {
        this.$notify.error(this.copy.phoneMissing);
        return;
      }
      this.processing = true;
      this.track('renewal_payment', {
        policy_number: this.renewal.policyNo,
        payment_method: this.paymentMethod,
      });
      try {
        const stkRes = await requestRenewalStkPush({
          batchId: this.batchId,
          payableAmount: this.amountDueNow,
          customerPhoneNumber: this.clientPhone,
          transactionReference: this.paymentReference,
          agentId: this.agentId,
          channel: this.paymentChannel,
        });
        if (!stkRes.success) {
          this.$notify.error(stkRes.message || 'M-Pesa request failed');
          this.processing = false;
          return;
        }
        this.paymentId = stkRes.data.paymentId;
        this.persistJourney();
        this.$notify.info(this.copy.stkSent);

        const pollRes = await pollUntilComplete(this.paymentId, this.paymentChannel);
        if (pollRes.success) {
          await this.completePayment(this.paymentId);
        } else {
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
          this.persistJourney();
          this.$notify.error(pollRes.message || 'Payment failed. You can pay via Paybill.');
        }
      } catch {
        this.paybillFallback = true;
        this.activePayTab = 'paybill';
        this.persistJourney();
        this.$notify.error('Payment could not be processed. You can pay via Paybill.');
      } finally {
        this.processing = false;
      }
    },

    async onValidatePaybill() {
      if (!this.paymentReference || !this.batchId) {
        this.$notify.error('Missing vehicle account number or renewal batch');
        return;
      }
      this.processing = true;
      this.track('renewal_paybill_validate', {
        policy_number: this.renewal.policyNo,
        account: this.paymentReference,
      });
      try {
        const res = await confirmRenewalPaybillPayment({
          batchId: this.batchId,
          accountNumber: this.paymentReference,
        });
        if (res.success) {
          await this.completePayment(this.paymentReference);
        } else {
          this.$notify.error(res.message || 'Payment not yet received. Complete Paybill and try again.');
        }
      } catch {
        this.$notify.error('Could not validate Paybill payment.');
      } finally {
        this.processing = false;
      }
    },

    async completePayment(transactionRef) {
      const progress = await getRenewalProgress(this.batchId);
      this.result = {
        newPolicyNo: progress.data?.policyNo || this.renewal.policyNo,
        transactionRef,
        batchId: this.batchId,
      };
      saveRenewalToSession(this.renewal, this.renewalType);
      this.step = 'success';
      this.persistJourney();
      this.track('renewal_completed', {
        policy_number: this.renewal.policyNo,
        transaction_ref: transactionRef,
      });
      if (this.isCustomer) {
        this.$store.dispatch('renewalReminders/refresh', { force: true });
      }
      this.$notify.success('Policy renewed successfully.');
    },
  },
  metaInfo: { title: 'Renew Policy' },
};
</script>

<style scoped>
.page-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px 0;
}
.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
.page-eyebrow {
  margin: 6px 0 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #00AEEF;
}
.stepper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 16px 24px;
}
.stepper-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #94A3B8;
  font-size: 0.85rem;
  font-weight: 600;
}
.stepper-item--active { color: #00AEEF; }
.stepper-item--done { color: #38A169; }
.stepper-node {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #E2E8F0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
}
.stepper-item--active .stepper-node {
  background: #00AEEF;
  color: white;
}
.stepper-item--done .stepper-node {
  background: #38A169;
  color: white;
}

.detail-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  padding: 24px 32px;
}
.detail-section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 8px 0;
}
.step-hint {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0 0 20px 0;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}
.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.detail-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1E4276;
}
.detail-value.highlight {
  color: #00AEEF;
  font-size: 1.1rem;
}
.checkbox-label {
  font-size: 0.9rem;
  color: #334155;
}

.risks-table-wrap {
  overflow-x: auto;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
}
.risks-table {
  width: 100%;
  border-collapse: collapse;
}
.risks-table th {
  text-align: left;
  padding: 10px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748B;
  text-transform: uppercase;
  background: #F8FAFC;
}
.risks-table td {
  padding: 12px 14px;
  font-size: 0.875rem;
  color: #334155;
  border-top: 1px solid #F1F5F9;
}
.benefits-panel {
  padding: 16px 18px 18px;
  border: 1px solid #E8EDF2;
  border-radius: 14px;
  background: #F8FAFC;
}
.benefits-panel__head {
  margin-bottom: 14px;
}
.benefits-panel__title {
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E4276;
}
.benefits-panel__hint {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: #64748B;
}

.premium-due {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 24px 28px;
  margin-bottom: 8px;
  border-radius: 14px;
  background: linear-gradient(135deg, #F0F9FF 0%, #E8F4FC 100%);
  border: 1px solid #D6EAF8;
}
.premium-due__label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748B;
}
.premium-due__amount {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  color: #1E4276;
  letter-spacing: -0.02em;
}
.premium-due__meta {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #64748B;
}

.payment-options {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.payment-option {
  flex: 1;
  max-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
  color: #1E4276;
}
.payment-option:hover {
  border-color: #00AEEF;
}
.payment-option--active {
  border-color: #00AEEF;
  background: #EBF8FF;
}
.section-sub-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 10px;
}
.tpo-full-note {
  font-size: 0.85rem;
  color: #64748B;
  padding: 12px 14px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
}
.continue-blocker {
  display: flex;
  align-items: center;
  margin: 12px 0 0;
  font-size: 0.8rem;
  color: #B45309;
}
.valuation-summary {
  font-size: 0.85rem;
  color: #334155;
  padding: 10px 14px;
  background: #ECFDF5;
  border: 1px solid #A7F3D0;
  border-radius: 10px;
}
.vehicle-premiums {
  list-style: none;
  margin-top: 12px;
  padding: 0;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
}
.vehicle-premiums__row {
  display: flex;
  justify-content: space-between;
  padding: 10px 14px;
  font-size: 0.85rem;
  color: #334155;
}
.vehicle-premiums__row + .vehicle-premiums__row {
  border-top: 1px solid #E2E8F0;
}
.plan-grid-v2 {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plan-card-v2 {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid #E8EDF2;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
}
.plan-card-v2:hover {
  border-color: #BAE6FD;
  background: #F8FDFF;
}
.plan-v2-active {
  border-color: #00AEEF !important;
  background: rgba(0, 174, 239, 0.04) !important;
  box-shadow: 0 0 0 1px #00AEEF;
}
.plan-v2-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.plan-v2-icon-active {
  background: rgba(0, 174, 239, 0.12) !important;
}
.plan-v2-body { flex: 1; }
.plan-v2-name {
  font-size: 0.84rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 2px;
}
.plan-v2-amount {
  font-size: 0.72rem;
  color: #64748B;
  margin: 0;
}
.plan-v2-check {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #00AEEF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 8px;
}
.installment-breakdown {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.inst-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px 10px;
  border-radius: 8px;
  min-width: 68px;
}
.inst-chip-now {
  background: rgba(0, 174, 239, 0.1);
  border: 1px solid rgba(0, 174, 239, 0.25);
}
.inst-chip-later {
  background: #F8FBFF;
  border: 1px solid #DEE9F5;
}
.inst-chip-pct {
  font-size: 0.65rem;
  font-weight: 800;
  color: #1E4276;
}
.inst-chip-label {
  font-size: 0.6rem;
  color: #94A3B8;
  font-weight: 600;
  margin: 1px 0;
}
.inst-chip-amount {
  font-size: 0.72rem;
  font-weight: 700;
  color: #00AEEF;
}
.inst-chip-later .inst-chip-amount { color: #475569; }
.inst-chip-connector {
  font-size: 0.7rem;
  color: #CBD5E1;
  font-weight: 700;
  flex-shrink: 0;
}
.pay-premium-footer {
  padding: 16px 18px;
  background: #F8FBFF;
  border: 1px solid #DEE9F5;
  border-radius: 12px;
}
.pay-prem-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  color: #64748B;
  margin-bottom: 6px;
}
.pay-prem-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px dashed #DEE9F5;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
}
.pay-prem-total-val {
  font-size: 1.25rem;
  font-weight: 800;
  color: #00AEEF;
}
.pay-amount-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #F8FAFC;
  border-radius: 10px;
  margin-bottom: 8px;
  font-size: 0.95rem;
  color: #64748B;
}
.pay-amount-box strong {
  font-size: 1.25rem;
  color: #00AEEF;
}
.premium-source {
  font-size: 0.78rem;
  color: #94A3B8;
  margin: 0 0 16px;
}
.pay-method-tabs {
  display: flex;
  gap: 8px;
}
.pay-method-tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  background: #fff;
  color: #64748B;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.pay-method-tab:hover {
  border-color: #BAE6FD;
  color: #1E4276;
}
.pay-method-tab--active {
  border-color: #1E4276 !important;
  background: #F0F4FA !important;
  color: #1E4276 !important;
  box-shadow: 0 0 0 1px #1E4276;
}
.pay-tab-mpesa-logo {
  height: 22px;
  width: auto;
  max-width: 28px;
  object-fit: contain;
  flex-shrink: 0;
}
.pay-tab-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pay-method-tab--active .pay-tab-icon-wrap {
  background: rgba(30, 66, 118, 0.1);
}
.mpesa-panel-card {
  background: #F8FBFF;
  border: 1.5px solid #DEE9F5;
  border-radius: 12px;
  padding: 16px;
}
.mpesa-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.mpesa-panel-logo {
  height: 36px;
  width: auto;
  max-width: 48px;
  object-fit: contain;
  flex-shrink: 0;
}
.mpesa-panel-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 2px;
}
.mpesa-panel-sub {
  font-size: 0.72rem;
  color: #64748B;
  margin: 0;
}
.stk-timeout-hint {
  font-size: 0.8rem;
  color: #C2410C;
  margin: 0 0 12px;
}
.phone-field {
  max-width: 360px;
}
.paybill-panel-card {
  border: 1.5px solid #E8EDF2;
  border-radius: 12px;
  overflow: hidden;
}
.paybill-panel-header {
  padding: 14px 16px;
  background: linear-gradient(90deg, #1E4276, #2A5A9E);
}
.paybill-panel-title { font-size: 0.88rem; font-weight: 700; color: white; margin: 0 0 2px; }
.paybill-panel-sub { font-size: 0.72rem; color: rgba(255,255,255,0.75); margin: 0; }
.paybill-ussd-steps { background: #F8FBFF; padding: 12px 16px; }
.paybill-ussd-title {
  font-size: 0.68rem;
  font-weight: 700;
  color: #64748B;
  text-transform: uppercase;
  margin: 0 0 8px;
}
.paybill-ussd-list { display: flex; flex-direction: column; gap: 5px; }
.paybill-ussd-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.45;
}
.paybill-ussd-num {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  background: #E8EDF2;
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1;
  color: #1E4276;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
}

.renewal-error {
  margin: 16px 0 0;
  padding: 12px 14px;
  border-radius: 8px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #991B1B;
  font-size: 0.875rem;
  line-height: 1.45;
}
.step-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #E8EDF2;
}
.step-actions--center {
  justify-content: center;
}

.success-card {
  text-align: center;
  padding: 40px 32px;
}
.success-icon-wrap {
  margin-bottom: 16px;
}
.success-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 8px 0;
}
.success-subtitle {
  font-size: 0.9rem;
  color: #94A3B8;
  margin: 0 0 28px 0;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}
.success-details {
  text-align: left;
  max-width: 420px;
  margin: 0 auto 28px;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
}
.success-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #F1F5F9;
  font-size: 0.875rem;
  color: #64748B;
}
.success-row:last-child {
  border-bottom: none;
}
.success-row strong {
  color: #1E4276;
  font-size: 0.9rem;
}

@media (max-width: 640px) {
  .page-title { font-size: 1.2rem; }
  .detail-card { padding: 18px 16px; border-radius: 14px; }
  .detail-grid { grid-template-columns: 1fr 1fr; gap: 14px 12px; }
  .detail-label { font-size: 0.68rem; }
  .detail-value { font-size: 0.9rem; overflow-wrap: anywhere; }
  .detail-value.highlight { font-size: 0.95rem; }

  .stepper {
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 4px;
    padding: 12px;
  }
  .stepper-item {
    flex: 1;
    flex-direction: column;
    gap: 4px;
    font-size: 0.7rem;
    text-align: center;
  }

  .risks-table th { padding: 8px 10px; font-size: 0.66rem; }
  .risks-table td { padding: 10px; font-size: 0.8rem; white-space: nowrap; }

  .premium-due { padding: 16px 18px; }
  .premium-due__amount { font-size: 1.6rem; }

  .step-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .step-actions--center {
    flex-direction: column;
  }
  .step-actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }

  .pay-method-tab { padding: 10px; gap: 8px; }

  .success-row { gap: 12px; }
  .success-row span { white-space: nowrap; }
  .success-row strong { text-align: right; word-break: break-word; }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}
.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
</style>
